import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  EisPermission,
  CommonPermission,
  RoleScope,
} from 'further-types/admin-user-role';
import { Typography, Button, Grid } from '@mui/material';

import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import {
  AppSimpleSelect,
  AppTextInput,
  FieldRow,
} from 'components/FormElements';
import api from 'lib/trpcClient';
import { ADMIN_USER_ROLES } from 'adminConstants/routes';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import Spacing from 'components/Spacing';
import Row from 'components/Layout/Row';
import PermissionGroupBox from './components/PermissionGroupBox';
import { FormData } from './types';
import {
  eisPermissionGroups,
  eisNonGroupedPermissions,
} from './permission-groups-config';
import { FirmSelector } from 'components/FirmSelector';
import { adminUserRole as rules } from 'further-ui/validations';

const AddEditRolePage: React.FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const [scope, setScope] = useState<RoleScope>(
    isSuperAdmin ? RoleScope.Global : RoleScope.Firm,
  );

  const createRole = api.adminUserRole.createEisRole.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Role created successfully.',
      successRedirect: ADMIN_USER_ROLES,
    }),
  );

  const updateRole = api.adminUserRole.updateEisRole.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Role updated successfully.',
      successRedirect: ADMIN_USER_ROLES,
    }),
  );

  const { id } = useParams<{ id?: string }>();
  const isRoleBeingEdited = !!id;

  const { data: role } = api.adminUserRole.get.useQuery(
    { roleId: id ?? '' },
    { enabled: isRoleBeingEdited },
  );

  // check the permissions are valid according to the enum (there may be old permissions in the DB that are no longer in the enum)
  const cleanPermissions = useMemo(() => {
    const validPermissions = new Set([
      ...Object.values(EisPermission),
      ...Object.values(CommonPermission),
    ]);

    if (!role?.permissions) {
      return [];
    }

    return role.permissions.filter(
      (permission): permission is EisPermission | CommonPermission =>
        validPermissions.has(permission as EisPermission | CommonPermission),
    );
  }, [role?.permissions]);

  useEffect(() => {
    if (role?.scope === RoleScope.Firm) {
      setScope(RoleScope.Firm);
    }
  }, [role?.scope]);

  const form = useForm<FormData>({
    values: isRoleBeingEdited
      ? {
          name: role?.name ?? '',
          description: role?.description ?? '',
          permissions: cleanPermissions,
          firmId: role?.firmId,
        }
      : {
          name: '',
          description: '',
          permissions: [],
          firmId,
        },
    resolver: zodResolver(
      rules.CreateEisRoleInput.superRefine((data, ctx) => {
        if (!isRoleBeingEdited && scope === RoleScope.Firm && !data.firmId) {
          ctx.addIssue({
            code: 'custom',
            message: 'Firm is required for firm specific roles.',
            path: ['firmId'],
          });
        }
      }),
    ),
  });

  const onCreateRole = (data: FormData) => {
    createRole.mutate(data);
  };

  const onUpdateRole = (data: FormData) => {
    if (!id) {
      return;
    }

    updateRole.mutate({
      roleId: id,
      description: data.description,
      permissions: data.permissions,
    });
  };

  return (
    <PageContainer heading={isRoleBeingEdited ? 'Edit Role' : 'Create Role'}>
      <PageContent withSpacing>
        <form
          onSubmit={form.handleSubmit(
            isRoleBeingEdited ? onUpdateRole : onCreateRole,
          )}
        >
          <Grid container spacing={2}>
            <Grid size={8}>
              <Spacing size="md">
                <FieldRow title="Role Name" centerTitle>
                  <Controller
                    control={form.control}
                    name="name"
                    disabled={isRoleBeingEdited}
                    render={({
                      fieldState: { error },
                      field: { ref, ...rest },
                    }) => (
                      <AppTextInput
                        {...rest}
                        error={!!error}
                        helperText={error?.message?.toString()}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow title="Description" centerTitle>
                  <Controller
                    control={form.control}
                    name="description"
                    render={({
                      fieldState: { error },
                      field: { ref, ...rest },
                    }) => (
                      <AppTextInput
                        {...rest}
                        error={!!error}
                        helperText={error?.message?.toString()}
                      />
                    )}
                  />
                </FieldRow>

                {isSuperAdmin ? (
                  <FieldRow title="Role scope" centerTitle>
                    <AppSimpleSelect
                      fullWidth
                      disabled={isRoleBeingEdited}
                      options={[
                        {
                          value: RoleScope.Global,
                          label: 'System-wide',
                        },
                        { value: RoleScope.Firm, label: 'Firm-specific' },
                      ]}
                      value={scope}
                      onChange={(e) => {
                        const newScope = e.target.value as RoleScope;
                        setScope(newScope);
                        if (newScope === RoleScope.Global) {
                          form.setValue('firmId', undefined);
                        }
                      }}
                    />
                  </FieldRow>
                ) : null}

                {isSuperAdmin && scope === RoleScope.Firm ? (
                  <FieldRow title="Firm" centerTitle>
                    <Controller
                      control={form.control}
                      disabled={isRoleBeingEdited}
                      name="firmId"
                      render={({
                        fieldState: { error },
                        field: { ref, value, ...rest },
                      }) => (
                        <FirmSelector
                          {...rest}
                          firmId={value}
                          error={!!error}
                          helperText={error?.message?.toString()}
                        />
                      )}
                    />
                  </FieldRow>
                ) : null}

                <Row justify="space-between">
                  <Typography variant="h2">Permissions</Typography>
                  <Button variant="contained" color="primary" type="submit">
                    {isRoleBeingEdited ? 'Save changes' : 'Create role'}
                  </Button>
                </Row>

                {eisPermissionGroups.map((group) => (
                  <PermissionGroupBox
                    key={group.groupName}
                    title={group.groupName}
                    description={group.groupDescription}
                    items={group.groupItems}
                    form={form}
                  />
                ))}

                <PermissionGroupBox
                  title={eisNonGroupedPermissions.groupName}
                  description={eisNonGroupedPermissions.groupDescription}
                  items={eisNonGroupedPermissions.groupItems}
                  form={form}
                />
              </Spacing>
            </Grid>
          </Grid>
        </form>
      </PageContent>
    </PageContainer>
  );
};

export default AddEditRolePage;
